<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import StatusIcon from './StatusIcon.vue'

const props = defineProps({
  state: {
    type: Object,
    required: true
  }
})

defineEmits(['retry', 'exit'])

const loadingPhrases = [
  '每一个点，都在寻找自己的位置',
  '正在把光影装进三维世界',
  '换个角度，熟悉的事物也会很新鲜',
  '空间正在从数据中慢慢显现',
  '马上就能走进这个模型了'
]
const phraseIndex = ref(0)
let phraseTimer = 0

function formatBytes(value) {
  const bytes = Math.max(0, Number(value) || 0)
  if (bytes < 1024) return `${Math.round(bytes)} B`

  const units = ['KB', 'MB', 'GB']
  let size = bytes / 1024
  let unitIndex = 0
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex += 1
  }

  const digits = size >= 100 ? 0 : size >= 10 ? 1 : 2
  return `${size.toFixed(digits)} ${units[unitIndex]}`
}

const sizeText = computed(() => {
  const loaded = Math.max(0, Number(props.state.loadedBytes) || 0)
  const total = Math.max(0, Number(props.state.totalBytes) || 0)
  if (total) return `${formatBytes(Math.min(loaded, total))} / ${formatBytes(total)}`
  if (loaded) return `已加载 ${formatBytes(loaded)}`
  return '正在获取文件大小'
})

const percentText = computed(() => {
  if (props.state.indeterminate || !Number.isFinite(props.state.progress)) return ''
  return `${Math.round(progressValue.value)}%`
})

const progressValue = computed(() => Math.max(0, Math.min(100, Number(props.state.progress) || 0)))
const progressStyle = computed(() => ({ width: `${progressValue.value}%` }))
const runnerStyle = computed(() => ({
  left: `clamp(14px, ${progressValue.value}%, calc(100% - 14px))`
}))
const loadingPhrase = computed(() => loadingPhrases[phraseIndex.value])
const retryLabel = computed(() => props.state.kind === 'network' ? '再试一次' : '重新加载')

function advancePhrase() {
  if (!props.state.visible || props.state.error) return
  phraseIndex.value = (phraseIndex.value + 1) % loadingPhrases.length
}

watch(() => props.state.visible, visible => {
  if (visible) phraseIndex.value = 0
})

onMounted(() => {
  phraseTimer = window.setInterval(advancePhrase, 3600)
})

onBeforeUnmount(() => {
  window.clearInterval(phraseTimer)
})
</script>

<template>
  <section
    class="loading"
    :class="{
      'is-hidden': !state.visible,
      'is-error': state.error,
      [`is-${state.kind || 'loading'}`]: true
    }"
    :role="state.error ? 'alert' : 'status'"
    :aria-live="state.error ? 'assertive' : 'polite'"
  >
    <div class="loading__content">
      <div class="loading__visual" aria-hidden="true">
        <template v-if="!state.error">
          <span class="loading__aura"></span>
          <i class="loading__orbit loading__orbit--one"></i>
          <i class="loading__orbit loading__orbit--two"></i>
          <i class="loading__orbit loading__orbit--three"></i>
          <span class="loading__cloud">
            <i v-for="index in 9" :key="index"></i>
          </span>
        </template>
        <span v-else class="loading__status-mark">
          <StatusIcon :name="state.kind" />
        </span>
      </div>
      <div class="loading__copy">
        <strong>{{ state.title }}</strong>
        <span v-if="state.text">{{ state.text }}</span>
      </div>
      <div v-if="!state.error" class="loading__progress" :class="{ 'is-indeterminate': state.indeterminate }">
        <span class="loading__runner" :style="state.indeterminate ? undefined : runnerStyle" aria-hidden="true">
          <span class="loading__robot">
            <i class="loading__robot-antenna"></i>
            <span class="loading__robot-head"><i></i></span>
            <span class="loading__robot-body"></span>
            <i class="loading__robot-arm loading__robot-arm--left"></i>
            <i class="loading__robot-arm loading__robot-arm--right"></i>
            <i class="loading__robot-leg loading__robot-leg--left"></i>
            <i class="loading__robot-leg loading__robot-leg--right"></i>
          </span>
        </span>
        <div
          class="loading__track"
          :class="{ 'is-indeterminate': state.indeterminate }"
          role="progressbar"
          :aria-valuenow="state.indeterminate ? undefined : progressValue"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label="模型加载进度"
        >
          <i :style="state.indeterminate ? undefined : progressStyle"></i>
        </div>
      </div>
      <div v-if="!state.error && state.showProgressDetails !== false" class="loading__progress-meta">
        <span>{{ sizeText }}</span>
        <strong v-if="percentText">{{ percentText }}</strong>
      </div>
      <div v-if="!state.error" class="loading__phrase" aria-hidden="true">
        <Transition name="loading-phrase" mode="out-in">
          <span :key="phraseIndex">{{ loadingPhrase }}</span>
        </Transition>
      </div>
      <div v-if="state.canRetry || state.canExit" class="loading__actions">
        <button v-if="state.canExit" class="loading__retry" :class="{ 'loading__retry--primary': !state.canRetry }" type="button" @click="$emit('exit')">返回</button>
        <button v-if="state.canRetry" class="loading__retry loading__retry--primary" type="button" @click="$emit('retry')">{{ retryLabel }}</button>
      </div>
    </div>
  </section>
</template>
